
// ######################################################

import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Switch } from "@mui/material";
import Card from "@mui/material/Card";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from "@mui/icons-material/History";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import WorkspaceService from "services/workspace-service";
import BillingService from "services/billing-service";
import Plan from "layouts/profile/components/Subscription/plan";
import History from "layouts/profile/components/Subscription/history";
import Alert from "@mui/material/Alert";

import { formatDateToDisplay } from "utils/dateUtils";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]
import UpgradePopup from "layouts/components/Upgrade"; // [NEW]
import { usePermissions } from "context"; // [FIXED]

const Subscription = () => {
  const [activeSubscription, setActiveSubscription] = useState({
    plan: "Loading...",
    billingCycle: "Loading...",
    startDate: "Loading...",
    renewalDate: "Loading...",
    endDate: "N/A",
    status: "Loading...",
    autoRenewal: false,
    stripe_subscription_id: null,
    stripe_customer_id: null,
    stripe_latest_invoice_id: null,
  });

  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);

  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State
  const [showRBACPopup, setShowRBACPopup] = useState(false); // [NEW] RBAC Popup State
  const [loading, setLoading] = useState(true);

  const { hasPermission } = usePermissions(); // [NEW]
  const canManageBilling = hasPermission("billing:manage"); // [NEW]

  // NEW: Hold the most recent subscription for determining billing visibility
  const [latestSubscriptionWithCustomer, setLatestSubscriptionWithCustomer] = useState(null);

  /**
   * Fetch Subscription Data
   */
  const fetchWorkspaceSubscription = async () => {
    try {
      setLoading(true);
      const subResponse = await WorkspaceService.fetchWorkspaceSubscription();

      // ... process subResponse (existing logic) ...
      setSubscriptionHistory(subResponse);

      // Extract Active Subscription
      const activeSub = (subResponse || []).find(sub => sub?.status?.toLowerCase() === "active");
      const pendingSub = (subResponse || []).find(sub => sub?.status?.toLowerCase() === "pending");

      if (activeSub) {
        setActiveSubscription({
          code: activeSub.code || "N/A",
          plan: activeSub.plan || "N/A",
          billingCycle: activeSub.billing_cycle || "N/A",
          startDate: (sessionStorage.getItem('demo_role_cache') === "demo_viewer")
            ? new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-")
            : (activeSub.start_date ? new Date(activeSub.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-") : "N/A"),
          endDate: activeSub.end_date ? new Date(activeSub.end_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-") : "N/A",
          renewalDate: activeSub.next_renewal_date ? new Date(activeSub.next_renewal_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-") : "N/A",
          status: activeSub.status.charAt(0).toUpperCase() + activeSub.status.slice(1),
          autoRenewal: activeSub.auto_renew || false,
          stripe_subscription_id: activeSub.stripe_subscription_id || null,
          stripe_customer_id: activeSub.stripe_customer_id || null,
          stripe_latest_invoice_id: activeSub.stripe_latest_invoice_id || null,
        });
      } else {
        setActiveSubscription(null);
      }

      setPendingSubscription(pendingSub || null);

      // NEW: Find the most recent updated subscription with a stripe_customer_id
      const sorted = [...(subResponse || [])].sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      const recentWithCustomer = sorted.find(x => x.stripe_customer_id);

      setLatestSubscriptionWithCustomer(recentWithCustomer || null);

    } catch (error) {
      console.error("Error fetching data:", error);
      setActiveSubscription(null); // Clear loading state on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceSubscription();

    // ✅ Listen for workspace switches (soft refresh)
    const onProfileUpdated = () => fetchWorkspaceSubscription();
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  const handleCancelSubscription = () => {
    // [NEW] RBAC Restriction
    if (!canManageBilling) {
      setShowRBACPopup(true);
      return;
    }

    // [NEW] Live Demo Restriction
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setShowDemoPopup(true);
      return;
    }
    setOpenCancelDialog(true);
  };

  const confirmCancelSubscription = async () => {
    setOpenCancelDialog(false);

    try {
      await WorkspaceService.cancelWorkspaceSubscription();

      setActiveSubscription((prev) => ({
        ...prev,
        autoRenewal: false,
        renewalDate: "N/A",
        endDate: prev.renewalDate,
        status: "Active (Cancelling)",
      }));

    } catch (error) {
      console.error("❌ Failed to cancel subscription:", error.message);
    }
  };

  const handleReactivateSubscription = async () => {
    // [NEW] RBAC Restriction
    if (!canManageBilling) {
      setShowRBACPopup(true);
      return;
    }

    try {
      await WorkspaceService.reactivateWorkspaceSubscription();

      setActiveSubscription((prev) => ({
        ...prev,
        autoRenewal: true,
        renewalDate: prev.endDate,
        endDate: "N/A",
        status: "Active",
      }));
    } catch (error) {
      console.error("❌ Failed to reactivate subscription:", error.message);
    }
  };

  const toggleAutoRenewal = async () => {
    // [NEW] RBAC Restriction
    if (!canManageBilling) {
      setShowRBACPopup(true);
      return;
    }

    const newAutoRenewalValue = !activeSubscription.autoRenewal;

    setActiveSubscription((prev) => ({ ...prev, autoRenewal: newAutoRenewalValue }));

    try {
      const updates = { "auto_renew": newAutoRenewalValue };
      await WorkspaceService.updateWorkspaceSubscription(updates);
    } catch (error) {
      console.error("❌ Failed to update auto-renewal:", error);
    }
  };

  // NEW: Billing Portal Handler
  const handleUpdateBilling = async () => {
    // [NEW] RBAC Restriction
    if (!canManageBilling) {
      setShowRBACPopup(true);
      return;
    }

    if (!latestSubscriptionWithCustomer?.stripe_customer_id) {
      console.error("No Stripe customer ID available for billing update.");
      return;
    }

    try {
      const url = await BillingService.createBillingPortalSession(
        latestSubscriptionWithCustomer.stripe_customer_id
      );

      if (url) {
        window.location.href = url;
      }

      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("❌ Failed to open Stripe Billing Portal:", error);
    }
  };

  return (
    <Card sx={{ mb: 3, mt: 7, overflow: "visible" }}>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDTypography variant="h6" color="white">
          Subscription Information
        </MDTypography>
      </MDBox>

      <MDBox p={2}>
        {/* PLAN DIALOG */}
        <Dialog
          open={showPlan}
          onClose={() => setShowPlan(false)}
          maxWidth="xl"
          fullWidth
          scroll="body"
        >
          <Plan
            onClose={() => setShowPlan(false)}
          />
        </Dialog>

        <MDBox sx={{ backgroundColor: "#f8f9fa", p: 2, borderRadius: 2 }}>
          {loading ? (
            <MDTypography variant="body2" color="textSecondary">
              Loading subscription details...
            </MDTypography>
          ) : activeSubscription ? (
            <Grid container spacing={2}>
              <Grid item xs={4}><MDTypography variant="subtitle2" color="textSecondary">Plan</MDTypography></Grid>
              <Grid item xs={8}><MDTypography variant="body2" fontWeight="bold">{activeSubscription.plan}</MDTypography></Grid>

              <Grid item xs={4}><MDTypography variant="subtitle2" color="textSecondary">Billing Cycle</MDTypography></Grid>
              <Grid item xs={8}><MDTypography variant="body2" fontWeight="bold">{activeSubscription.billingCycle}</MDTypography></Grid>

              <Grid item xs={4}><MDTypography variant="subtitle2" color="textSecondary">Start Date</MDTypography></Grid>
              <Grid item xs={8}><MDTypography variant="body2" fontWeight="bold">{activeSubscription.startDate}</MDTypography></Grid>

              <Grid item xs={4}>
                <MDTypography variant="subtitle2" color="textSecondary">
                  {activeSubscription.plan.toLowerCase() === "trial" ? "Trial End Date" : "Next Renewal Date"}
                </MDTypography>
              </Grid>
              <Grid item xs={8}><MDTypography variant="body2" fontWeight="bold">{activeSubscription.renewalDate}</MDTypography></Grid>

              <Grid item xs={4}><MDTypography variant="subtitle2" color="textSecondary">Status</MDTypography></Grid>
              <Grid item xs={8}>
                <MDTypography
                  variant="body2"
                  fontWeight="bold"
                  color={activeSubscription.status === "Active" ? "success.main" : "error.main"}
                >
                  {activeSubscription.status}
                </MDTypography>
              </Grid>
            </Grid>
          ) : (
            <MDTypography>No active subscription</MDTypography>
          )}

          {!activeSubscription?.autoRenewal && activeSubscription?.endDate && (
            <MDBox mt={2}>
              <Alert severity="info">
                Your subscription will end on {formatDateToDisplay(activeSubscription.endDate)}.
              </Alert>
            </MDBox>
          )}


          {/* HISTORY */}
          <Dialog
            open={showHistory}
            onClose={() => setShowHistory(false)}
            maxWidth="xl"
            fullWidth
            scroll="body"
          >
            <History
              subscriptionHistory={subscriptionHistory}
              onClose={() => setShowHistory(false)}
            />
          </Dialog>


          {/* ACTION BUTTONS */}
          <MDBox display="flex" justifyContent="flex-end" mt={2} gap={1}>

            <Button
              startIcon={<EditIcon />}
              color="primary"
              onClick={() => {
                if (!canManageBilling) {
                  setShowRBACPopup(true);
                  return;
                }
                setShowPlan(prev => !prev);
              }}
            >
              {showPlan ? "HIDE PLAN OPTIONS" : "CHANGE PLAN"}
            </Button>

            {/* NEW: SHOW ONLY IF WE HAVE STRIPE CUSTOMER */}
            {latestSubscriptionWithCustomer?.stripe_customer_id && (
              <Button startIcon={<EditIcon />} color="secondary" onClick={handleUpdateBilling}>
                UPDATE BILLING
              </Button>
            )}

            <Button startIcon={<HistoryIcon />} color="secondary" onClick={() => setShowHistory(prev => !prev)}>
              {showHistory ? "HIDE HISTORY" : "PLAN HISTORY"}
            </Button>

            {/* CANCEL / RE-ACTIVATE (Hide for Trial) */}
            {activeSubscription?.plan?.toLowerCase() !== "trial" && (
              activeSubscription?.autoRenewal ? (
                <Button startIcon={<CancelIcon />} color="error" onClick={handleCancelSubscription}>
                  CANCEL
                </Button>
              ) : (
                <Button startIcon={<CheckCircleIcon />} color="success" onClick={handleReactivateSubscription}>
                  RE-ACTIVATE
                </Button>
              )
            )}

          </MDBox>
        </MDBox>

        {/* CANCEL DIALOG */}
        <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
          <DialogTitle>Confirm Subscription Cancellation</DialogTitle>
          <DialogContent>
            <MDTypography variant="subtitle2">
              Are you sure you want to cancel your subscription? You may lose access to premium features.
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCancelDialog(false)} color="primary">Keep Subscription</Button>
            <Button onClick={confirmCancelSubscription} color="error">Cancel Subscription</Button>
          </DialogActions>
        </Dialog>

        {/* [NEW] Live Demo Restriction Popup */}
        <LiveDemoRestriction
          open={showDemoPopup}
          onClose={() => setShowDemoPopup(false)}
          featureName="Canceling Subscription"
        />

        {/* [NEW] RBAC Restriction Popup */}
        <UpgradePopup
          type="rbac"
          open={showRBACPopup}
          onClose={() => setShowRBACPopup(false)}
          roleRequired="Owner"
          message="Only Workspace Owners can modify subscription and billing settings."
        />
      </MDBox>
    </Card>
  );
};

export default Subscription;
